import { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Loader2, Download, Ban, CheckCircle2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS_STYLE = {
  draft: 'bg-stone-100 text-stone-600',
  issued: 'bg-blue-100 text-blue-700',
  partially_settled: 'bg-amber-100 text-amber-700',
  settled: 'bg-emerald-100 text-emerald-700',
  void: 'bg-red-100 text-red-600',
};

export function BranchInvoicesTab({ branches = [] }) {
  const [invoices, setInvoices] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/branch-invoices');
      setInvoices(data.invoices || []);
      setOutstanding(data.outstanding_total || 0);
    } catch { toast.error('Failed to load invoices'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const bname = (id) => branches.find(b => b.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="branch-invoices-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Wallet size={15} className="text-orange-500" />
          Outstanding across branches: <span className="font-semibold text-foreground" data-testid="binv-outstanding-total">{outstanding.toLocaleString()}</span>
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)} data-testid="binv-new-btn"><Plus size={14} className="mr-1" /> New Invoice</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="binv-empty">No branch invoices yet. Create one from completed branch orders.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="binv-table">
            <thead><tr className="border-b bg-muted/50 text-xs text-muted-foreground uppercase">
              {['Invoice', 'From → To', 'Total', 'Outstanding', 'Status', ''].map(h => <th key={h} className="text-left p-2.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-b hover:bg-muted/40" data-testid={`binv-row-${inv.id}`}>
                  <td className="p-2.5 font-medium">{inv.invoice_number}</td>
                  <td className="p-2.5">{bname(inv.from_branch_id)} → {bname(inv.to_branch_id)}</td>
                  <td className="p-2.5">{Number(inv.total).toLocaleString()}</td>
                  <td className="p-2.5">{Number(inv.outstanding).toLocaleString()}</td>
                  <td className="p-2.5"><Badge className={`text-[10px] ${STATUS_STYLE[inv.status] || ''}`}>{inv.status.replace('_', ' ')}</Badge></td>
                  <td className="p-2.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDetail(inv)} data-testid={`binv-open-${inv.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && <NewInvoiceDialog branches={branches} onClose={() => setNewOpen(false)} onCreated={(inv) => { setNewOpen(false); load(); setDetail(inv); }} />}
      {detail && <InvoiceDetailDialog invoice={detail} bname={bname} onClose={() => setDetail(null)} onChanged={(inv) => { load(); setDetail(inv); }} />}
    </div>
  );
}

function NewInvoiceDialog({ branches, onClose, onCreated }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadCands = async () => {
    if (!from || !to || from === to) { toast.error('Pick two different branches'); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/branch-invoices/candidates', { params: { from_branch_id: from, to_branch_id: to } });
      setCands(data.candidates || []);
      if (!data.candidates?.length) toast.info('No un-invoiced completed orders for this pair');
    } catch (e) { toast.error('Failed to load orders'); }
    setLoading(false);
  };

  const create = async () => {
    const ids = Object.keys(sel).filter(k => sel[k]);
    if (!ids.length) { toast.error('Select at least one order'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/branch-invoices', { from_branch_id: from, to_branch_id: to, order_ids: ids });
      toast.success(`Draft ${data.invoice_number} created`);
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    setCreating(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="binv-new-dialog">
        <DialogHeader><DialogTitle>New Branch Invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">From (issuer)</label>
              <Select value={from} onValueChange={setFrom}>
                <SelectTrigger className="h-9" data-testid="binv-from"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To (recipient)</label>
              <Select value={to} onValueChange={setTo}>
                <SelectTrigger className="h-9" data-testid="binv-to"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={loadCands} disabled={loading} data-testid="binv-load-orders">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load completed orders'}
          </Button>
          {cands.length > 0 && (
            <div className="max-h-56 overflow-y-auto border rounded-md divide-y" data-testid="binv-candidates">
              {cands.map(c => (
                <label key={c.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted/40">
                  <input type="checkbox" checked={!!sel[c.id]} onChange={e => setSel({ ...sel, [c.id]: e.target.checked })} data-testid={`binv-cand-${c.id}`} />
                  <span className="flex-1">{c.ref} · {c.date} · {c.items} items</span>
                  <span className="text-muted-foreground">cost {Number(c.cost).toLocaleString()}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={creating} data-testid="binv-create">{creating ? 'Creating…' : 'Create Draft'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDetailDialog({ invoice, bname, onClose, onChanged }) {
  const [inv, setInv] = useState(invoice);
  const [lines, setLines] = useState(invoice.line_items || []);
  const [invDisc, setInvDisc] = useState(invoice.invoice_discount || { type: null, value: 0 });
  const [busy, setBusy] = useState(false);
  const [settleAmt, setSettleAmt] = useState('');
  const isDraft = inv.status === 'draft';

  const setLine = (i, k, v) => { const n = [...lines]; n[i] = { ...n[i], [k]: v }; setLines(n); };
  const setLineDisc = (i, type, value) => { const n = [...lines]; n[i] = { ...n[i], discount: { type, value: Number(value) || 0 } }; setLines(n); };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/branch-invoices/${inv.id}`, { line_items: lines, invoice_discount: invDisc });
      setInv(data); setLines(data.line_items); toast.success('Saved'); onChanged(data);
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };
  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/branch-invoices/${inv.id}/${path}`, body || {});
      const next = data.invoice || data;
      setInv(next); toast.success(okMsg); onChanged(next);
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };
  const exportFile = (fmt) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/branch-invoices/${inv.id}/export?format=${fmt}`, '_blank');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="binv-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {inv.invoice_number}
            <Badge className={`text-[10px] ${STATUS_STYLE[inv.status] || ''}`}>{inv.status.replace('_', ' ')}</Badge>
            <span className="text-xs font-normal text-muted-foreground">{bname(inv.from_branch_id)} → {bname(inv.to_branch_id)}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-muted-foreground border-b"><th className="text-left p-1.5">Item</th><th className="p-1.5">Qty</th><th className="p-1.5">Unit</th><th className="p-1.5">Disc</th><th className="p-1.5 text-right">Total</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b" data-testid={`binv-line-${i}`}>
                  <td className="p-1.5">{l.name}{l.day ? <span className="text-[10px] text-muted-foreground ml-1">{l.day}</span> : ''}</td>
                  <td className="p-1.5 text-center">{l.qty}</td>
                  <td className="p-1.5 w-24">
                    {isDraft ? <Input type="number" value={l.unit_price} onChange={e => setLine(i, 'unit_price', Number(e.target.value))} className="h-7 text-xs" data-testid={`binv-line-price-${i}`} /> : Number(l.unit_price).toLocaleString()}
                  </td>
                  <td className="p-1.5 w-28">
                    {isDraft ? (
                      <div className="flex gap-1">
                        <Input type="number" value={l.discount?.value || 0} onChange={e => setLineDisc(i, l.discount?.type || 'fixed', e.target.value)} className="h-7 text-xs w-14" data-testid={`binv-line-disc-${i}`} />
                        <Select value={l.discount?.type || 'fixed'} onValueChange={v => setLineDisc(i, v, l.discount?.value || 0)}>
                          <SelectTrigger className="h-7 text-xs w-14"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="fixed">$</SelectItem><SelectItem value="pct">%</SelectItem></SelectContent>
                        </Select>
                      </div>
                    ) : (l.discount?.type ? `${l.discount.value}${l.discount.type === 'pct' ? '%' : ''}` : '-')}
                  </td>
                  <td className="p-1.5 text-right">{Number(l.line_total).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-3 text-sm">
            <span className="text-muted-foreground">Invoice discount</span>
            {isDraft ? (
              <div className="flex gap-1">
                <Input type="number" value={invDisc.value || 0} onChange={e => setInvDisc({ ...invDisc, value: Number(e.target.value), type: invDisc.type || 'fixed' })} className="h-7 text-xs w-16" data-testid="binv-invoice-disc" />
                <Select value={invDisc.type || 'fixed'} onValueChange={v => setInvDisc({ ...invDisc, type: v })}>
                  <SelectTrigger className="h-7 text-xs w-14"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="fixed">$</SelectItem><SelectItem value="pct">%</SelectItem></SelectContent>
                </Select>
              </div>
            ) : <span>{inv.invoice_discount_amount || 0}</span>}
          </div>
          <div className="text-right space-y-0.5 text-sm">
            <div>Subtotal: <b>{Number(inv.subtotal).toLocaleString()}</b></div>
            <div>Total: <b data-testid="binv-total">{Number(inv.total).toLocaleString()}</b></div>
            {inv.status !== 'draft' && <>
              <div>Settled: {Number(inv.settled_amount || 0).toLocaleString()}</div>
              <div className="text-orange-600">Outstanding: <b data-testid="binv-detail-outstanding">{Number(inv.outstanding).toLocaleString()}</b></div>
            </>}
            {inv.tax?.mode === 'internal' && <div className="text-[11px] text-muted-foreground">Internal transfer — non-taxable</div>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => exportFile('pdf')} data-testid="binv-export-pdf"><Download size={13} className="mr-1" />PDF</Button>
          <Button variant="ghost" size="sm" onClick={() => exportFile('excel')} data-testid="binv-export-excel"><Download size={13} className="mr-1" />Excel</Button>
          {isDraft && <Button variant="outline" size="sm" onClick={saveDraft} disabled={busy} data-testid="binv-save">Save</Button>}
          {isDraft && <Button size="sm" onClick={() => act('issue', {}, 'Invoice issued')} disabled={busy} data-testid="binv-issue"><CheckCircle2 size={14} className="mr-1" />Issue</Button>}
          {(inv.status === 'issued' || inv.status === 'partially_settled') && (
            <div className="flex gap-1 items-center">
              <Input type="number" placeholder="Amount" value={settleAmt} onChange={e => setSettleAmt(e.target.value)} className="h-8 w-24 text-xs" data-testid="binv-settle-amount" />
              <Button size="sm" onClick={() => { act('settle', { amount: Number(settleAmt), payment_mode: 'cash' }, 'Settlement recorded'); setSettleAmt(''); }} disabled={busy} data-testid="binv-settle"><Wallet size={14} className="mr-1" />Settle</Button>
            </div>
          )}
          {inv.status !== 'void' && Number(inv.settled_amount || 0) === 0 && (
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => act('void', {}, 'Invoice voided')} disabled={busy} data-testid="binv-void"><Ban size={14} className="mr-1" />Void</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
